import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/Dialog";
import type { ProviderUnit, StartWalkInSessionInput } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { calculateIntervalTotalAmount } from "../../lib/provider-pricing";
import { formatEgp } from "../provider-orders/order-status";

const DURATIONS = [30, 60, 90, 120, 180];

export function formatMinutes(minutes: number, t: (key: string, values?: Record<string, string | number>) => string) {
  if (minutes < 60) return t("{minutes} min", { minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? t("{hours}h", { hours }) : t("{hours}h {minutes}m", { hours, minutes: rest });
}

interface Props {
  unit: ProviderUnit | null;
  venueId: string;
  timezone: string;
  busy: boolean;
  onClose: () => void;
  onStart: (payload: StartWalkInSessionInput) => Promise<void>;
}

/** Start a walk-in on one unit: who, how long, and what it will cost. */
export function WalkInDialog({ unit, venueId, timezone, busy, onClose, onStart }: Props) {
  const { t, language } = useAppPreferences();
  const [name, setName] = useState("");
  const [minutes, setMinutes] = useState(60);
  const [custom, setCustom] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!unit) return;
    setName("");
    setMinutes(60);
    setCustom("");
    setShowMore(false);
    setPhone("");
    setEmail("");
    setNote("");
  }, [unit?.id]);

  const customMinutes = Number.parseInt(custom, 10);
  const chosen = custom.trim() ? customMinutes : minutes;
  const valid = Number.isFinite(chosen) && chosen >= 15 && chosen <= 720;
  const now = Date.now();
  const estimate = unit && valid
    ? calculateIntervalTotalAmount(unit, timezone, new Date(now).toISOString(), new Date(now + chosen * 60000).toISOString())
    : null;

  const start = async () => {
    if (!unit || !valid) return;
    await onStart({
      venue_id: venueId,
      unit_id: unit.id,
      customer_name: name.trim() || null,
      customer_phone: phone.trim() || null,
      customer_email: email.trim() || null,
      notes: note.trim() || null,
      planned_duration_minutes: chosen,
    });
  };

  return (
    <Dialog open={unit !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto">
        <DialogTitle>{unit ? t("Start walk-in: {unit}", { unit: unit.name }) : null}</DialogTitle>
        <DialogDescription>{t("For guests without a booking. The timer starts now.")}</DialogDescription>
        <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); void start(); }}>
          <label className="grid gap-1">
            <span className="ops-label">{t("Guest name (optional)")}</span>
            <input autoFocus className="ops-input" maxLength={255} onChange={(event) => setName(event.target.value)} placeholder={t("Walk-in guest")} value={name} />
          </label>
          <fieldset className="grid gap-1.5">
            <legend className="ops-label mb-1">{t("How long?")}</legend>
            <div className="flex flex-wrap gap-1.5">
              {DURATIONS.map((value) => (
                <button
                  aria-pressed={!custom.trim() && minutes === value}
                  className="ops-button"
                  data-size="sm"
                  data-variant={!custom.trim() && minutes === value ? "primary" : "secondary"}
                  key={value}
                  onClick={() => { setMinutes(value); setCustom(""); }}
                  type="button"
                >
                  {formatMinutes(value, t)}
                </button>
              ))}
              <input
                aria-label={t("Other length in minutes")}
                className="ops-input w-28"
                inputMode="numeric"
                max={720}
                min={15}
                onChange={(event) => setCustom(event.target.value)}
                placeholder={t("Other (min)")}
                step={15}
                type="number"
                value={custom}
              />
            </div>
            {!valid ? <p className="ops-copy text-xs" data-tone="danger">{t("Choose between 15 minutes and 12 hours.")}</p> : null}
          </fieldset>
          {estimate !== null ? (
            <p className="walkin-estimate">
              {t("About {amount} for {duration}", { amount: formatEgp(estimate, language), duration: formatMinutes(chosen, t) })}
              <span className="ops-copy block text-xs">{t("You can add time or end early. The bill uses the actual time played.")}</span>
            </p>
          ) : null}
          <button aria-expanded={showMore} className="ops-button justify-self-start" data-size="sm" data-variant="secondary" onClick={() => setShowMore((value) => !value)} type="button">
            {showMore ? t("Hide phone, email, note") : t("Add phone, email or note")}
          </button>
          {showMore ? (
            <div className="grid gap-2">
              <label className="grid gap-1">
                <span className="ops-label">{t("Phone")}</span>
                <input className="ops-input" inputMode="tel" maxLength={32} onChange={(event) => setPhone(event.target.value)} placeholder="+20 100 000 0000" value={phone} />
              </label>
              <label className="grid gap-1">
                <span className="ops-label">{t("Email")}</span>
                <input className="ops-input" onChange={(event) => setEmail(event.target.value)} placeholder="guest@example.com" type="email" value={email} />
              </label>
              <label className="grid gap-1">
                <span className="ops-label">{t("Note")}</span>
                <input className="ops-input" maxLength={1000} onChange={(event) => setNote(event.target.value)} value={note} />
              </label>
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button className="ops-button" data-variant="secondary" onClick={onClose} type="button">{t("Cancel")}</button>
            <button className="ops-button" data-variant="primary" disabled={busy || !valid} type="submit">
              {busy ? t("Starting...") : t("Start timer")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
